import React, { Fragment, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { PopupBanner } from '../../types';

interface ServiceEntryPopupProps {
  popup: PopupBanner;
  isOpen: boolean;
  onClose: () => void;
  onCloseForWeek: () => void;
}

const ServiceEntryPopup: React.FC<ServiceEntryPopupProps> = ({
  popup,
  isOpen,
  onClose,
  onCloseForWeek,
}) => {
  const [dontShowForWeek, setDontShowForWeek] = useState(false);

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }, []);

  const imageUrl = useMemo(() => {
    if (isMobile) {
      return popup.mobile_image_url || popup.web_image_url || '';
    }
    return popup.web_image_url || popup.mobile_image_url || '';
  }, [isMobile, popup.mobile_image_url, popup.web_image_url]);

  const linkNoticeText = popup.link_notice_text?.trim();

  const handleClose = () => {
    if (dontShowForWeek) {
      onCloseForWeek();
      return;
    }
    onClose();
  };

  const handleImageClick = () => {
    if (!popup.link_url) return;
    if (popup.link_target === '_self') {
      window.location.href = popup.link_url;
      return;
    }
    window.open(popup.link_url, popup.link_target || '_blank', 'noopener,noreferrer');
  };

  if (typeof document === 'undefined' || !isOpen || !imageUrl) {
    return null;
  }

  return createPortal(
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-[1px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-6 sm:py-10">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-[560px] overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                  <div>
                    <Dialog.Title className="text-base font-bold text-gray-900">
                      {popup.title}
                    </Dialog.Title>
                    {linkNoticeText && (
                      <p className="mt-1 text-xs text-gray-500">{linkNoticeText}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                    aria-label="팝업 닫기"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="bg-gray-50 px-4 py-4 sm:px-5">
                  <button
                    type="button"
                    onClick={handleImageClick}
                    disabled={!popup.link_url}
                    className={`block w-full overflow-hidden rounded-2xl border border-gray-200 bg-white ${
                      popup.link_url ? 'cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    <img
                      src={imageUrl}
                      alt={popup.alt_text || popup.title}
                      className="h-auto w-full object-contain"
                    />
                  </button>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={dontShowForWeek}
                      onChange={(event) => setDontShowForWeek(event.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D]"
                    />
                    <span>일주일 동안 보지 않기</span>
                  </label>

                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-[#1B365D] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#162c4b]"
                  >
                    닫기
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>,
    document.body,
  );
};

export default ServiceEntryPopup;
